/**
 * Org structure — Outline.
 *
 * The indented tree this screen has always had, rebuilt on the shared model.
 *
 * A chart shows shape; an outline shows order. For "read down the whole force
 * and find the one thing", a dense list beats any chart — which is why this is
 * still here. Rebuilding it on the same model rather than its own fetch means
 * one search box, one vacancy filter, one compare set and one set of counts
 * across all five views, instead of two screens that could disagree.
 */
import { useEffect, useMemo, useRef } from 'react';
import Icon from './org-icons';
import { headColour, headLabel, lineageOf, matches, nf, vacancyPct, vacancyTone, type OrgModel } from './org-model';
import styles from './org-structure.module.css';

interface OutlineProps {
  model: OrgModel;
  collapsed: Set<string>;
  onToggleCollapse: (id: string) => void;
  selected: string | null;
  onSelect: (id: string | null) => void;
  query: string;
  vacantOnly: boolean;
  /** List only the selected unit's line of command, as the chart does. */
  isolate: boolean;
  compareSet: string[];
  onToggleCompare: (id: string) => void;
  onOpenUnit: (id: string) => void;
  /** One-shot: scroll this unit's row into view. The outline's answer to Fit. */
  focus?: { id: string; token: number } | null;
}

const OrgOutline = ({
  model,
  collapsed,
  onToggleCollapse,
  selected,
  onSelect,
  query,
  vacantOnly,
  isolate,
  compareSet,
  onToggleCompare,
  onOpenUnit,
  focus,
}: OutlineProps) => {
  const listRef = useRef<HTMLDivElement>(null);

  /**
   * The selected unit's line of command, or null for the whole tree — the same
   * scope the chart lays out, so switching view keeps you where you were.
   */
  const only = useMemo(
    () => (isolate && selected && model.byId[selected] ? lineageOf(model, selected) : null),
    [isolate, selected, model]
  );

  /** Depth-first, skipping what is folded — the same `collapsed` set the charts use. */
  const rows = useMemo(() => {
    const out: string[] = [];
    const walk = (id: string) => {
      if (only && !only.has(id)) return;
      out.push(id);
      if (!collapsed.has(id)) model.byId[id].kids.forEach(walk);
    };
    model.roots.forEach(walk);
    return out;
  }, [model, collapsed, only]);

  // Dimming only means something when something is left sharp — the same rule
  // the chart follows, so a query that matches nothing does not grey the lot.
  const filtering = useMemo(() => {
    if (!query && !vacantOnly) return false;
    return model.ids.some((id) => matches(model.byId[id], query, vacantOnly));
  }, [model, query, vacantOnly]);

  /*
   * The columns, decided by what is in the data.
   *
   * A fixed seven-column grid is why this list looked wrong however much the
   * rows were polished: on this force the officer, posts and unfilled columns
   * are empty for every single unit, and they were still holding 18 to 23rem —
   * between a third and a half of the table — to print a dash 1,377 times. The
   * unit names, the one thing anybody reads a list like this for, were squeezed
   * into 15rem beside them.
   *
   * So a column exists when at least one unit in scope has something to put in
   * it, and the width it was holding goes back to the names. Nothing is
   * removed: the day a head post is recorded anywhere, the officer column comes
   * back on its own.
   */
  const cols = useMemo(() => {
    const anyHead = model.ids.some((id) => model.byId[id].headState !== 'unknown');
    const c: Array<{ k: string; head: string; w: string; min: number }> = [
      { k: 'unit', head: 'Unit', w: 'minmax(22rem, 1fr)', min: 22 },
    ];
    if (anyHead) c.push({ k: 'who', head: 'Officer in charge', w: 'minmax(9rem, 15rem)', min: 9 });
    if (model.hasHeadcount) c.push({ k: 'people', head: 'People', w: '5rem', min: 5 });
    if (model.hasPostData) {
      c.push({ k: 'posts', head: 'Posts', w: '5.5rem', min: 5.5 });
      c.push({ k: 'unfilled', head: 'Unfilled', w: '5rem', min: 5 });
    }
    c.push({ k: 'subs', head: 'Sub-units', w: '5.5rem', min: 5.5 });
    c.push({ k: 'act', head: '', w: '4.6rem', min: 4.6 });
    return c;
  }, [model]);

  const grid = { gridTemplateColumns: cols.map((c) => c.w).join(' ') };
  const show = (k: string) => cols.some((c) => c.k === k);
  /*
   * The list only has to be as wide as the columns that survived — and it
   * should not grow much past that either. On this force only three columns
   * survive, and stretching them to a wide display left about a metre of
   * white between a unit's name and its one number. The names get the slack,
   * up to a point; past that the sheet stops and centres.
   */
  const tight = cols.reduce((n, c) => n + c.min, 0) + cols.length * 0.5 + 2;
  const size = { minWidth: `${tight}rem`, maxWidth: `${Math.min(74, tight + 18)}rem` };

  const focusToken = focus?.token ?? -1;
  useEffect(() => {
    if (!focus) return;
    listRef.current
      ?.querySelector(`[data-row="${focus.id}"]`)
      ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    // Keyed on the token alone so an unrelated re-render cannot re-scroll you.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusToken]);

  return (
    /* The details are a popup the page puts up, not a panel docked in here:
       one details surface for all five views, and a list this dense has no
       room to give a third of its width away. */
    <div className={styles.osplit}>
      <div className={styles.oscroll} ref={listRef}>
        <div className={styles.outline} style={size}>
        <div className={`${styles.orow} ${styles.ohead}`} style={grid}>
          {cols.map((c) => (
            <span key={c.k} className={c.k === 'unit' || c.k === 'who' || c.k === 'act' ? undefined : styles.onum}>
              {c.head}
            </span>
          ))}
        </div>

        {rows.map((id) => {
          const u = model.byId[id];
          const vac = u.headState === 'vacant';
          const dim = filtering && !matches(u, query, vacantOnly);
          const isCollapsed = collapsed.has(id);
          const stored = compareSet.includes(id);
          return (
            <div
              key={id}
              data-row={id}
              /* Marks where a new top-level command starts, so the seven of
                 them read as seven blocks rather than one run of 1,377 rows.
                 The CSS bands and rules these; nothing else reads it. */
              data-top={u.lvl === 0 ? '' : undefined}
              className={
                `${styles.orow}${selected === id ? ` ${styles.osel}` : ''}` +
                `${dim ? ` ${styles.odim}` : ''}`
              }
              style={grid}
            >
              <span className={styles.oname}>
                {/* One rail per level above this row.
                    Indentation alone told you a row was deeper than the one
                    before it and nothing else — at level five you could not
                    tell which of four possible parents it hung from. The rails
                    draw the branch it is actually on, which is the one thing a
                    flat list of 1,377 units cannot otherwise say. */}
                {Array.from({ length: u.lvl }, (_, i) => (
                  <span key={i} className={styles.orail} aria-hidden="true" />
                ))}
                {u.kids.length ? (
                  <button
                    type="button"
                    className={`${styles.otog}${isCollapsed ? '' : ` ${styles.otogopen}`}`}
                    aria-label={isCollapsed ? `Expand ${u.name}` : `Collapse ${u.name}`}
                    title={isCollapsed ? `Show what reports to ${u.name}` : `Fold ${u.name} away`}
                    aria-expanded={!isCollapsed}
                    onClick={() => onToggleCollapse(id)}
                  >
                    <Icon name="chev" className={styles.ic} size={12} />
                  </button>
                ) : (
                  <span className={styles.otog} />
                )}
                <span
                  className={styles.sq}
                  style={{ background: headColour(u) }}
                />
                {/* Weight by depth: the top of the force reads as the top of
                    the force. Capped at three bands, because past that the
                    difference stops being legible and starts being noise. */}
                <button
                  type="button"
                  className={styles.olabel}
                  data-lvl={Math.min(u.lvl, 3)}
                  title={`Open the details for ${u.name}`}
                  onClick={() => onSelect(id)}
                >
                  {/* The Inactive flag sits OUTSIDE the ellipsised name.
                      Inside it, a long name pushed the flag past the clip and
                      a dormant unit read as a running one — the one thing on
                      this row that must never be the part that gets cut. */}
                  <span className={styles.oline}>
                    <b>{u.name}</b>
                    {!u.isActive && <span className={styles.inactive}>Inactive</span>}
                  </span>
                  <i>
                    {u.code} · {u.typeLabel}
                  </i>
                </button>
              </span>

              {/* A dash where this particular unit has nothing, in the muted
                  ink — so a real number is the thing that catches the eye as
                  you scan the column, not the absence of one. */}
              {show('who') && (
                <span className={`${styles.ooff}${vac ? ` ${styles.vacname}` : ''}`}>
                  {u.headState === 'unknown' ? <span className={styles.onil}>—</span> : headLabel(u)}
                  {/* Guarded on the post, not on the person: where a head post
                      stands empty, naming it says WHICH job is vacant. */}
                  {u.headRank ? <em> · {u.headRank}</em> : null}
                </span>
              )}
              {show('people') && (
                <span className={styles.onum}>
                  {u.people > 0 ? nf(u.people) : <span className={styles.onil}>—</span>}
                </span>
              )}
              {show('posts') && (
                <span className={styles.onum}>
                  {u.rPosts ? `${nf(u.rFilled)}/${nf(u.rPosts)}` : <span className={styles.onil}>—</span>}
                </span>
              )}
              {show('unfilled') && (
                <span className={styles.onum} style={{ color: vacancyTone(vacancyPct(u)) }}>
                  {vacancyPct(u) === null ? <span className={styles.onil}>—</span> : `${vacancyPct(u)}%`}
                </span>
              )}
              <span className={styles.onum}>
                {u.subUnits ? nf(u.subUnits) : <span className={styles.onil}>—</span>}
              </span>

              <span className={styles.oact}>
                <button
                  type="button"
                  className={`${styles.pin}${stored ? ` ${styles.pinon}` : ''}`}
                  title={stored ? 'Remove from the compare set' : 'Add to the compare set'}
                  aria-label={stored ? 'Remove from the compare set' : 'Add to the compare set'}
                  onClick={() => onToggleCompare(id)}
                >
                  {stored ? '✓' : '+'}
                </button>
                <button
                  type="button"
                  className={styles.iact}
                  title="Open the unit record"
                  aria-label={`Open ${u.name}`}
                  onClick={() => onOpenUnit(id)}
                >
                  <Icon name="out" className={styles.ic} size={13} />
                </button>
              </span>
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
};

export default OrgOutline;
